import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { Routes } from '@/_shared/constants/routes';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { AlertBadge } from '../../AlertBadge';

type Props = {
  invitedTotal: number;
  completedTotal: number;
  linkAssessment?: string;
  templateName?: string;
};

export const RecapAssessment: FC<Props> = ({
  invitedTotal,
  completedTotal,
  linkAssessment,
  templateName,
}) => {
  const t = useTranslations('surveysShared.recapAssessment.overview');
  const navigate = useNavigate();
  const gapXs = useThemeValue('space', 'xs');

  const { genericResponsiveValue } = useResponsiveStyle();

  const { completedPercentage, remainingTotal } = useMemo(() => {
    const remaining = invitedTotal - completedTotal;
    const rate =
      completedTotal > 0
        ? `${Math.min(100, Math.max(0, ((completedTotal ?? 0) / (invitedTotal ?? 0)) * 100)).toFixed(0)}%`
        : '0%';

    return {
      remainingTotal: remaining,
      completedPercentage: rate,
    };
  }, [invitedTotal, completedTotal]);

  const goToAssessment = useCallback(() => {
    navigate(Routes.SurveyDetail.replace(':surveyId', linkAssessment || ''));
  }, [linkAssessment, navigate]);

  return (
    <Card mt='m' mb='s' display='flex' flexDirection='column'>
      <Box
        style={{ gap: gapXs }}
        display='grid'
        gridTemplateColumns={genericResponsiveValue({
          breakPoints: {
            tablet: 'repeat(2, 1fr)',
            mobile: 'repeat(1, 1fr)',
          },
          defaultValue: linkAssessment ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)',
        })}
      >
        <Box alignItems='flex-start' width='100%' flex={1}>
          <Typography variant='p3/label' mb='xs'>
            {t('invited')}
          </Typography>
          <Typography variant='special'>{invitedTotal}</Typography>
        </Box>
        <Box width='100%' flex={1}>
          <Typography variant='p3/label' mb='xs'>
            {t('completed')}
          </Typography>
          <Typography variant='special'>{completedTotal}</Typography>
        </Box>
        <Box width='100%' flex={1}>
          <Typography variant='p3/label' mb='xs'>
            {t('toComplete')}
          </Typography>
          <Typography variant='special' color={remainingTotal > 0 ? 'error' : 'success'}>
            {remainingTotal}
          </Typography>
        </Box>
        <Box width='100%' flex={1}>
          <Typography variant='p3/label' mb='xs'>
            {t('completionRate')}
          </Typography>
          <Typography variant='special'>{completedPercentage}</Typography>
        </Box>
        {linkAssessment && (
          <Box display='flex' alignItems='center' width='100%' flex={1}>
            <Button onClick={goToAssessment} variant='positive'>
              {t('linkAssessment')}
            </Button>
          </Box>
        )}
      </Box>
      {completedTotal === 0 && (
        <AlertBadge
          iconName='lightBulb'
          type='transparent'
          label={templateName ? t('infoWithName', { templateName }) : t('info')}
          containerProps={{ mt: 'm' }}
        />
      )}
    </Card>
  );
};
